import { useState, useEffect, useMemo, useRef } from 'react';
import { useCustomers, useDeleteCustomer, useUpdateCustomer } from '../hooks/useCustomers';
import { useServerPagination } from '../hooks/useServerPagination';
import Card from '../components/Card';
import Pagination from '../components/Pagination';
import Button from '../components/Button';
import { Link, useSearchParams } from 'react-router-dom';
import type { Customer } from '../lib/api';
import { useRole } from '../hooks/useRole';
import Input from '../components/Input';
import MobileCard from '../components/MobileCard';
import { Pencil, Trash2, MapPin, Package } from 'lucide-react';
import Skeleton from '../components/Skeleton';
import { searchParamsEffectivelyEqual } from '../lib/listUrlParams';

type CustomerSortCol = 'customer' | 'customer_location' | 'pack_count';

const CUSTOMER_SORT_KEYS = new Set<string>([
  'customer',
  'customer_location',
  'pack_count',
]);

export default function CustomersTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBootRef = useRef<{
    page: number;
    customerSort: { col: CustomerSortCol; desc: boolean } | null;
  } | null>(null);
  if (urlBootRef.current === null) {
    const sp = new URLSearchParams(window.location.search);
    const page = Math.max(0, parseInt(sp.get('page') || '0', 10) || 0);
    const raw = sp.get('sort') || '';
    const col = CUSTOMER_SORT_KEYS.has(raw) ? (raw as CustomerSortCol) : null;
    const customerSort = col
      ? { col, desc: sp.get('order') === 'desc' }
      : null;
    urlBootRef.current = { page, customerSort };
  }
  const urlBoot = urlBootRef.current;
  const pagination = useServerPagination(20, { initialPage: urlBoot.page, initialLastKeys: [null] });
  const deleteCustomer = useDeleteCustomer();
  const updateCustomer = useUpdateCustomer();
  const { isEditor } = useRole();
  const [deleteConfirm, setDeleteConfirm] = useState<{ customer: string } | null>(null);
  const [editLocation, setEditLocation] = useState<{ customer: string, location: string } | null>(null);
  const [customerSort, setCustomerSort] = useState<{
    col: CustomerSortCol;
    desc: boolean;
  } | null>(urlBoot.customerSort);

  const { data, isLoading, error } = useCustomers();

  const clients = data?.items ?? [];

  const sortedClients = useMemo(() => {
    const list = [...clients];
    if (!customerSort) return list;
    const d = customerSort.desc ? -1 : 1;
    list.sort((a, b) => {
      if (customerSort.col === 'pack_count') {
        return (a.pack_count - b.pack_count) * d;
      }
      const va = String(
        customerSort.col === 'customer' ? a.customer : a.customer_location ?? ''
      ).toLowerCase();
      const vb = String(
        customerSort.col === 'customer' ? b.customer : b.customer_location ?? ''
      ).toLowerCase();
      return va.localeCompare(vb) * d;
    });
    return list;
  }, [clients, customerSort]);

  const pagedClients = useMemo(() => {
    const start = pagination.currentPage * pagination.pageSize;
    return sortedClients.slice(start, start + pagination.pageSize);
  }, [sortedClients, pagination.currentPage, pagination.pageSize]);

  const toggleCustomerSort = (col: CustomerSortCol) => {
    setCustomerSort((prev) => {
      if (!prev || prev.col !== col) return { col, desc: false };
      if (!prev.desc) return { col, desc: true };
      return null;
    });
    pagination.reset();
  };

  const sortIndicator = (col: CustomerSortCol) => {
    if (!customerSort || customerSort.col !== col) return '⇅';
    return customerSort.desc ? '↓' : '↑';
  };

  const handleDelete = async (customer: string) => {
    try {
      await deleteCustomer.mutateAsync(customer);
      setDeleteConfirm(null);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to delete customer');
    }
  };

  const handleUpdateLocation = async () => {
    if (!editLocation) return;
    try {
      await updateCustomer.mutateAsync({
        customer: editLocation.customer,
        data: { customer_location: editLocation.location }
      });
      setEditLocation(null);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to update location');
    }
  };

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(sortedClients.length / pagination.pageSize) - 1
    );
    if (sortedClients.length > 0 && pagination.currentPage > maxPage) {
      pagination.goToPage(maxPage);
    }
  }, [
    sortedClients.length,
    pagination.pageSize,
    pagination.currentPage,
    pagination.goToPage,
  ]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(pagination.currentPage));
    if (customerSort) {
      next.set('sort', customerSort.col);
      next.set('order', customerSort.desc ? 'desc' : 'asc');
    } else {
      next.delete('sort');
      next.delete('order');
    }
    if (!searchParamsEffectivelyEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, pagination.currentPage, customerSort]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex gap-4">
              <Skeleton variant="circle" className="w-12 h-12 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">Error Loading Data</h4>
              <p className="text-sm text-red-700">
                {error instanceof Error ? error.message : 'Failed to load customers.'}
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const totalRows = sortedClients.length;
  const totalPages =
    totalRows > 0 ? Math.ceil(totalRows / pagination.pageSize) : null;
  const canGoNext =
    (pagination.currentPage + 1) * pagination.pageSize < totalRows;

  const handlePageChange = (direction: 'next' | 'prev' | 'first' | 'last') => {
    if (direction === 'next') {
      pagination.nextPage();
    } else if (direction === 'prev') {
      pagination.previousPage();
    } else if (direction === 'first') {
      pagination.reset();
    }
  };

  const handlePageSizeChange = (size: number) => {
    pagination.setPageSize(size);
    pagination.reset();
  };

  // Mobile Card Component
  const CustomerCard = ({ customer }: { customer: Customer }) => (
    <MobileCard>
      <div className="space-y-3">
        {/* Header: Customer Name & Actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate">{customer.customer}</h3>
            {customer.customer_location && (
              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{customer.customer_location}</span>
              </div>
            )}
          </div>
          {isEditor && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setEditLocation({
                  customer: customer.customer,
                  location: customer.customer_location || ''
                })}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit location"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeleteConfirm({ customer: customer.customer })}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete customer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Pack Count Badge */}
        <div className="flex items-center gap-2 mt-2">
          <Link
            to={`/packs?q=${encodeURIComponent(customer.customer)}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
          >
            <Package className="w-3.5 h-3.5" />
            <span>{customer.pack_count} pack{customer.pack_count !== 1 ? 's' : ''}</span>
          </Link>
        </div>
      </div>
    </MobileCard>
  );

  // Desktop Table Row
  const DesktopTable = () => (
    <div className="hidden md:block overflow-x-auto rounded-xl shadow-sm border border-gray-100">
      <table className="min-w-full bg-white rounded-xl overflow-hidden">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <button
                type="button"
                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-blue-600"
                onClick={() => toggleCustomerSort('customer')}
              >
                Customer <span className="text-gray-400">{sortIndicator('customer')}</span>
              </button>
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <button
                type="button"
                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-blue-600"
                onClick={() => toggleCustomerSort('customer_location')}
              >
                Location <span className="text-gray-400">{sortIndicator('customer_location')}</span>
              </button>
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              <button
                type="button"
                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-blue-600"
                onClick={() => toggleCustomerSort('pack_count')}
              >
                Packs <span className="text-gray-400">{sortIndicator('pack_count')}</span>
              </button>
            </th>
            {isEditor && (
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {pagedClients.map((client) => (
            <tr key={client.customer} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="font-semibold text-gray-900">{client.customer}</div>
              </td>
              <td className="px-6 py-4 text-gray-600">{client.customer_location || '-'}</td>
              <td className="px-6 py-4">
                <Link
                  to={`/packs?q=${encodeURIComponent(client.customer)}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors"
                >
                  <Package className="w-3 h-3" />
                  {client.pack_count} pack{client.pack_count !== 1 ? 's' : ''}
                </Link>
              </td>
              {isEditor && (
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setEditLocation({
                        customer: client.customer,
                        location: client.customer_location || ''
                      })}
                      className="text-blue-600 hover:bg-blue-50 text-sm px-3 py-1"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteConfirm({ customer: client.customer })}
                      className="text-red-600 hover:bg-red-50 text-sm px-3 py-1"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {clients.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-500 font-medium">No customers found</p>
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile View */}
          <div className="md:hidden space-y-3">
            {pagedClients.map((client) => (
              <CustomerCard key={client.customer} customer={client} />
            ))}
          </div>

          {/* Desktop View */}
          <DesktopTable />

          {/* Pagination */}
          <Card className="mt-4">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={totalPages}
              total={totalRows}
              pageSize={pagination.pageSize}
              canGoNext={canGoNext}
              canGoPrevious={pagination.canGoPrevious}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </Card>
        </>
      )}

      {/* Edit Location Modal */}
      {editLocation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Edit Location</h3>
              <p className="text-sm text-gray-500 mb-4">Customer: {editLocation.customer}</p>
              <Input
                label="Location"
                value={editLocation.location}
                onChange={(e) => setEditLocation({ ...editLocation, location: e.target.value })}
                placeholder="Enter location"
              />
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleUpdateLocation}
                  disabled={updateCustomer.isPending}
                  className="flex-1"
                >
                  {updateCustomer.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setEditLocation(null)}
                  disabled={updateCustomer.isPending}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="p-4">
              <h3 className="text-lg font-bold text-red-900 mb-2">Delete Customer</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">"{deleteConfirm.customer}"</span>? This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDelete(deleteConfirm.customer)}
                  disabled={deleteCustomer.isPending}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleteCustomer.isPending ? 'Deleting...' : 'Delete'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteCustomer.isPending}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

